import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import StatusPill from "@/components/StatusPill";
import { fmtMoney, fmtDate, titleCase } from "@/lib/format";
import { SnoozeCard } from "@/components/SnoozeCard";
import { ShieldCheck, Receipt, Calendar, Pause, MessageSquare, LogIn, CreditCard, CheckCircle2, Zap, ZapOff, KeyRound, Lock, MailCheck, FastForward, Clock, Truck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { company } from "@/lib/branding";

/**
 * Customer Portal — supports both modes:
 *   1. Password auth (preferred) — /portal/login + /portal/register + reset flow
 *   2. Legacy email-only lookup — for customers who haven't set a password yet
 */
export default function PortalDashboard() {
    const [mode, setMode] = useState("signin"); // signin | first-time | forgot | reset
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [resetToken, setResetToken] = useState("");
    const [customer, setCustomer] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [overrides, setOverrides] = useState([]);
    const [nextPickup, setNextPickup] = useState(null);
    const [savedCard, setSavedCard] = useState(null);  // {brand,last4,exp_month,exp_year} | null
    const [busy, setBusy] = useState(false);
    const [params] = useSearchParams();
    const nav = useNavigate();

    useEffect(() => {
        // Resume session via stored email if password was used previously
        const saved = localStorage.getItem("rd_portal_email");
        if (saved) {
            setEmail(saved);
            silentLookup(saved);
        }
        const sid = params.get("session_id");
        if (sid) pollPaymentStatus(sid, 0);
        const autopaySid = params.get("autopay_session");
        if (autopaySid && saved) confirmAutoPay(autopaySid, saved);
        const token = params.get("token");
        if (token) {
            setResetToken(token);
            setMode("reset");
        }
        // eslint-disable-next-line
    }, []);

    const silentLookup = async (em) => {
        try {
            const { data } = await api.post("/portal/lookup", { email: em });
            setCustomer(data.customer);
            setInvoices(data.invoices || []);
            setOverrides(data.overrides || []);
            setNextPickup(data.next_pickup || null);
            fetchSavedCard();
        } catch {
            // expected if a password is set — they'll log in via signin form
        }
    };

    const signIn = async () => {
        if (!email || !password) return toast.error("Email and password required");
        setBusy(true);
        try {
            const { data } = await api.post("/portal/login", { email, password });
            setCustomer(data.customer);
            setInvoices(data.invoices || []);
            setOverrides(data.overrides || []);
            setNextPickup(data.next_pickup || null);
            if (data.access_token) localStorage.setItem("rd_token", data.access_token);
            localStorage.setItem("rd_portal_email", email);
            fetchSavedCard();
            toast.success("Welcome back");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invalid email or password");
        } finally { setBusy(false); }
    };

    const firstTime = async () => {
        if (!email || password.length < 8) return toast.error("Password must be at least 8 characters");
        if (password !== confirmPw) return toast.error("Passwords do not match");
        setBusy(true);
        try {
            const { data } = await api.post("/portal/register", { email, password });
            setCustomer(data.customer);
            setInvoices(data.invoices || []);
            setOverrides(data.overrides || []);
            setNextPickup(data.next_pickup || null);
            if (data.access_token) localStorage.setItem("rd_token", data.access_token);
            localStorage.setItem("rd_portal_email", email);
            fetchSavedCard();
            toast.success("Password set — you're signed in");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create password");
        } finally { setBusy(false); }
    };

    const forgot = async () => {
        if (!email) return toast.error("Email required");
        setBusy(true);
        try {
            await api.post("/portal/request-reset", { email });
            toast.success("If that email exists, a reset link is on its way");
            setMode("signin");
        } catch {
            toast.error("Could not send reset");
        } finally { setBusy(false); }
    };

    const resetPassword = async () => {
        if (password.length < 8) return toast.error("Password must be at least 8 characters");
        if (password !== confirmPw) return toast.error("Passwords do not match");
        setBusy(true);
        try {
            await api.post("/portal/reset-password", { token: resetToken, new_password: password });
            toast.success("Password reset — please sign in");
            setMode("signin");
            setPassword(""); setConfirmPw("");
            nav("/portal", { replace: true });
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
        } finally { setBusy(false); }
    };

    const confirmAutoPay = async (sessionId, em) => {
        try {
            const { data: lookupData } = await api.post("/portal/lookup", { email: em });
            const cust = lookupData.customer;
            const { data } = await api.post(`/autopay/setup/${cust.id}/confirm`, { session_id: sessionId });
            toast[data.status === "completed" ? "success" : "info"](
                data.status === "completed" ? "AutoPay enabled" : "AutoPay setup finalizing"
            );
            silentLookup(em);
            fetchSavedCard();
            nav("/portal", { replace: true });
        } catch {
            toast.error("Could not confirm AutoPay setup");
        }
    };

    const enableAutoPay = async () => {
        try {
            const { data } = await api.post(`/autopay/setup/${customer.id}`,
                { origin_url: window.location.origin });
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start AutoPay");
        }
    };

    // Fetch the customer's saved card (if any) so we can offer one-tap enable.
    // Only works when the portal session has an auth token (i.e. after
    // /portal/login or /portal/register). Legacy email-only lookups skip this.
    const fetchSavedCard = async () => {
        if (!localStorage.getItem("rd_token")) return;
        try {
            const { data } = await api.get("/portal/payment-method");
            setSavedCard(data.card || null);
        } catch {
            setSavedCard(null);
        }
    };

    const quickEnableAutoPay = async () => {
        if (!customer) return;
        setBusy(true);
        try {
            await api.post(`/autopay/quick-enable/${customer.id}`);
            toast.success("AutoPay is on — you're all set");
            setCustomer((c) => ({ ...c, autopay: true }));
        } catch (e) {
            const detail = formatApiErrorDetail(e.response?.data?.detail);
            if (e.response?.status === 400) {
                // No card on file after all — fall back to Setup Checkout
                toast.info("Let's save a card first");
                enableAutoPay();
            } else {
                toast.error(detail || "Could not enable AutoPay");
            }
        } finally { setBusy(false); }
    };

    const disableAutoPay = async () => {
        if (!customer) return;
        if (!window.confirm("Turn off AutoPay? You'll need to pay each invoice manually. Your saved card stays on file.")) return;
        setBusy(true);
        try {
            await api.post(`/autopay/disable/${customer.id}`);
            toast.success("AutoPay turned off — your card is still on file for next time");
            setCustomer((c) => ({ ...c, autopay: false }));
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not turn off");
        } finally { setBusy(false); }
    };

    const pollPaymentStatus = async (sessionId, attempts) => {
        if (attempts >= 6) return;
        try {
            const { data } = await api.get(`/payments/status/${sessionId}`);
            if (data.payment_status === "paid") {
                toast.success("Payment confirmed");
                const saved = localStorage.getItem("rd_portal_email");
                if (saved) silentLookup(saved);
                nav("/portal", { replace: true });
                return;
            }
            setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
        } catch { setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000); }
    };

    const payInvoice = async (invId) => {
        try {
            const { data } = await api.post(`/payments/checkout/invoice/${invId}`,
                { origin_url: window.location.origin });
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
        }
    };

    const logoutPortal = () => {
        localStorage.removeItem("rd_portal_email");
        setCustomer(null); setInvoices([]); setOverrides([]); setNextPickup(null); setPassword(""); setConfirmPw("");
        setMode("signin");
    };

    // ─────────────────────────────────────────────────────────────────────
    if (!customer) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="portal-login">
                <Card className="w-full max-w-md">
                    <CardContent className="p-8">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
                                <ShieldCheck className="w-5 h-5 text-primary-foreground" />
                            </div>
                            <div className="font-heading font-bold" data-testid="portal-company-name">{company()}</div>
                            <div className="overline text-[9px]">Customer Portal</div>
                        </div>

                        {mode === "reset" ? (
                            <div className="space-y-3">
                                <div className="overline mb-2">Reset password</div>
                                <h2 className="font-heading text-2xl font-bold tracking-tight mb-1">Choose a new password</h2>
                                <p className="text-sm text-muted-foreground mb-6">Pick something at least 8 characters.</p>
                                <Label className="text-xs">New password</Label>
                                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                                       data-testid="portal-reset-password" />
                                <Label className="text-xs">Confirm new password</Label>
                                <Input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                                       data-testid="portal-reset-confirm" />
                                <Button className="w-full mt-2" onClick={resetPassword} disabled={busy} data-testid="portal-reset-btn">
                                    <KeyRound className="w-4 h-4 mr-1" />Set new password
                                </Button>
                            </div>
                        ) : (
                            <Tabs value={mode} onValueChange={setMode} className="w-full">
                                <TabsList className="grid grid-cols-3 w-full" data-testid="portal-tabs">
                                    <TabsTrigger value="signin" data-testid="portal-tab-signin">Sign in</TabsTrigger>
                                    <TabsTrigger value="first-time" data-testid="portal-tab-first">First time</TabsTrigger>
                                    <TabsTrigger value="forgot" data-testid="portal-tab-forgot">Forgot</TabsTrigger>
                                </TabsList>

                                <TabsContent value="signin" className="space-y-3 pt-5">
                                    <h2 className="font-heading text-3xl font-bold tracking-tight mb-1">Welcome back.</h2>
                                    <p className="text-sm text-muted-foreground">Enter your account email + password.</p>
                                    <Label className="text-xs">Email</Label>
                                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                                           placeholder="you@example.com" data-testid="portal-email" />
                                    <Label className="text-xs">Password</Label>
                                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                                           onKeyDown={(e) => e.key === "Enter" && signIn()}
                                           data-testid="portal-password" />
                                    <Button className="w-full mt-2" onClick={signIn} disabled={busy}
                                            data-testid="portal-login-btn">
                                        <LogIn className="w-4 h-4 mr-2" />Sign in
                                    </Button>
                                </TabsContent>

                                <TabsContent value="first-time" className="space-y-3 pt-5">
                                    <h2 className="font-heading text-2xl font-bold tracking-tight mb-1">Set up your portal.</h2>
                                    <p className="text-sm text-muted-foreground">
                                        Use the email on your {company()} account and pick a password.
                                    </p>
                                    <Label className="text-xs">Email</Label>
                                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                                           data-testid="portal-first-email" />
                                    <Label className="text-xs">Choose a password</Label>
                                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                                           data-testid="portal-first-pw" />
                                    <Label className="text-xs">Confirm password</Label>
                                    <Input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                                           data-testid="portal-first-pw2" />
                                    <Button className="w-full mt-2" onClick={firstTime} disabled={busy}
                                            data-testid="portal-first-btn">
                                        <Lock className="w-4 h-4 mr-2" />Create password
                                    </Button>
                                </TabsContent>

                                <TabsContent value="forgot" className="space-y-3 pt-5">
                                    <h2 className="font-heading text-2xl font-bold tracking-tight mb-1">Forgot your password?</h2>
                                    <p className="text-sm text-muted-foreground">We'll email you a reset link.</p>
                                    <Label className="text-xs">Email</Label>
                                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                                           data-testid="portal-forgot-email" />
                                    <Button className="w-full mt-2" onClick={forgot} disabled={busy}
                                            data-testid="portal-forgot-btn">
                                        <MailCheck className="w-4 h-4 mr-2" />Send reset link
                                    </Button>
                                </TabsContent>
                            </Tabs>
                        )}

                        <div className="mt-6 pt-6 border-t border-border text-xs text-muted-foreground space-y-1">
                            <div>New customer? <Link to="/signup" className="text-primary hover:underline">Sign up for service →</Link></div>
                            <div>Staff member? <Link to="/login" className="text-primary hover:underline">Office login →</Link></div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // ─────────────────────────────────────────────────────────────────────
    const openInvoices = invoices.filter((i) => i.status === "open" || i.status === "overdue");
    const paidInvoices = invoices.filter((i) => i.status === "paid");

    return (
        <div className="min-h-screen bg-background" data-testid="portal-dashboard">
            <header className="border-b border-border">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
                            <ShieldCheck className="w-4 h-4 text-primary-foreground" />
                        </div>
                        <div>
                            <div className="font-heading font-bold text-sm">{company()}</div>
                            <div className="overline text-[9px]">Customer Portal</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                        <span className="text-muted-foreground">{customer.name}</span>
                        <Button variant="ghost" size="sm" onClick={logoutPortal} data-testid="portal-logout">Sign out</Button>
                    </div>
                </div>
            </header>

            <div className="max-w-5xl mx-auto px-6 py-10 space-y-6">
                <div>
                    <div className="overline mb-2">Account · {customer.account_number}</div>
                    <h1 className="font-heading text-4xl font-bold tracking-tighter">Hello, {customer.name.split(" ")[0]}.</h1>
                    <p className="text-sm text-muted-foreground mt-2">
                        Your {customer.service_plan.replace(/_/g, " ")} service runs every {customer.route_day}.
                    </p>
                </div>

                <CoverageBadge
                    customer={customer}
                    openInvoices={openInvoices}
                    onPay={payInvoice}
                />

                {nextPickup && (
                    <Card data-testid="portal-next-pickup" className="border-primary/30 bg-primary/5">
                        <CardContent className="p-5 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div className="w-11 h-11 rounded-md bg-primary/15 flex items-center justify-center">
                                    <Truck className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <div className="overline mb-1">Next pickup</div>
                                    <div className="font-heading text-lg font-bold">
                                        {titleCase(nextPickup.route_day)}
                                        {nextPickup.eta && (
                                            <span className="ml-2 text-primary tabular font-mono text-base">
                                                {formatEta(nextPickup.eta)}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                        {nextPickup.eta
                                            ? `Estimated arrival — please have your cart curbside by 6 AM.`
                                            : `Regular ${titleCase(nextPickup.route_day)} route.`}
                                    </div>
                                </div>
                            </div>
                            {nextPickup.sequence != null && (
                                <div className="text-right">
                                    <div className="overline text-[9px]">Stop</div>
                                    <div className="text-lg font-mono font-semibold">#{nextPickup.sequence + 1}</div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                <Card data-testid="portal-autopay-card" className={customer.autopay ? "border-emerald-500/30 bg-emerald-500/5" : ""}>
                    <CardContent className="p-5 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            {customer.autopay ? <Zap className="w-6 h-6 text-emerald-500" /> : <ZapOff className="w-6 h-6 text-muted-foreground" />}
                            <div>
                                <div className="font-heading text-lg font-bold">
                                    {customer.autopay
                                        ? "AutoPay is on"
                                        : (savedCard ? "One tap to enable AutoPay" : "Enable AutoPay")}
                                </div>
                                <div className="text-xs text-muted-foreground mt-0.5">
                                    {customer.autopay ? (
                                        savedCard ? (
                                            <span data-testid="portal-autopay-card-label">
                                                Charging {titleCase(savedCard.brand)} •••• {savedCard.last4} each quarter.
                                                Expires {String(savedCard.exp_month).padStart(2, "0")}/{String(savedCard.exp_year).slice(-2)}.
                                            </span>
                                        ) : (
                                            "Your invoices are charged automatically each quarter."
                                        )
                                    ) : savedCard ? (
                                        <span data-testid="portal-autopay-card-label">
                                            Use your {titleCase(savedCard.brand)} •••• {savedCard.last4} — we'll charge it each quarter so you're never late.
                                        </span>
                                    ) : (
                                        "Save a card on file and we'll charge it quarterly so you never get a late fee."
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                            {customer.autopay ? (
                                <>
                                    <Button variant="outline" size="sm"
                                            onClick={enableAutoPay} data-testid="portal-autopay-update-btn">
                                        <CreditCard className="w-3.5 h-3.5 mr-1" />
                                        Update card
                                    </Button>
                                    <Button variant="ghost" size="sm"
                                            onClick={disableAutoPay}
                                            disabled={busy}
                                            data-testid="portal-autopay-off-btn">
                                        Turn off
                                    </Button>
                                </>
                            ) : savedCard ? (
                                <>
                                    <Button size="sm"
                                            onClick={quickEnableAutoPay}
                                            disabled={busy}
                                            data-testid="portal-autopay-quick-enable-btn">
                                        <Zap className="w-4 h-4 mr-1" />
                                        Turn on AutoPay
                                    </Button>
                                    <Button variant="ghost" size="sm"
                                            onClick={enableAutoPay}
                                            data-testid="portal-autopay-btn">
                                        Use different card
                                    </Button>
                                </>
                            ) : (
                                <Button variant="default"
                                        onClick={enableAutoPay} data-testid="portal-autopay-btn">
                                    <CreditCard className="w-4 h-4 mr-1" />
                                    Set up AutoPay
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Snooze reminders — only for password-authenticated portal
                    customers (legacy email-lookup sessions can't hit the
                    /portal/snoozes endpoints). */}
                <SnoozeCard enabled={!!localStorage.getItem("rd_token")} />

                {overrides.length > 0 && (
                    <Card data-testid="portal-overrides" className="border-violet-500/30 bg-violet-500/5">
                        <CardContent className="p-5">
                            <div className="flex items-start gap-4">
                                <FastForward className="w-6 h-6 text-violet-500 shrink-0 mt-1" />
                                <div className="flex-1">
                                    <div className="font-heading text-lg font-bold">Upcoming schedule changes</div>
                                    <div className="text-xs text-muted-foreground mt-0.5 mb-3">
                                        We've shifted your pickup so you don't miss it on a holiday week.
                                    </div>
                                    <div className="space-y-2">
                                        {overrides.map((o) => (
                                            <div key={o.id} className="flex items-center justify-between gap-3 py-2 px-3 rounded-md bg-background/60 border border-border"
                                                 data-testid={`portal-override-${o.id}`}>
                                                <div>
                                                    <div className="text-sm font-medium">{o.reason}</div>
                                                    <div className="text-[11px] text-muted-foreground">
                                                        Originally {fmtDate(o.original_date)}
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="overline text-[9px]">New pickup</div>
                                                    <div className="text-sm font-semibold font-mono">{fmtDate(o.shifted_date)}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Action icon={Receipt} label="Pay a bill" testid="portal-pay-action" onClick={() => openInvoices[0] && payInvoice(openInvoices[0].id)} disabled={!openInvoices.length} />
                    <Action icon={Calendar} label="Request extra pickup" testid="portal-extra-action" onClick={() => toast.info("Office has been notified")} />
                    <Action icon={Pause} label="Vacation hold" testid="portal-hold-action" onClick={() => toast.info("Office will confirm your hold")} />
                    <Action icon={MessageSquare} label="Contact office" testid="portal-contact-action" onClick={() => toast.info("Office will reach out within 1 business day")} />
                </div>

                <Card data-testid="portal-invoices">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="overline mb-1">Open balances</div>
                                <h3 className="font-heading text-lg font-semibold">Pay invoices</h3>
                            </div>
                        </div>
                        {openInvoices.length === 0 ? (
                            <div className="flex items-center gap-3 p-4 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-400 text-sm">
                                <CheckCircle2 className="w-4 h-4" />You're all caught up — no open invoices.
                            </div>
                        ) : (
                            <div className="divide-y divide-border">
                                {openInvoices.map((inv) => (
                                    <div key={inv.id} className="py-3 flex items-center justify-between gap-3">
                                        <div>
                                            <div className="text-sm font-mono">{inv.invoice_number || inv.id?.slice(0, 8)}</div>
                                            <div className="text-xs text-muted-foreground">Due {fmtDate(inv.due_date)}</div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="text-right">
                                                <div className="text-sm font-semibold tabular">{fmtMoney(inv.amount)}</div>
                                                <StatusPill status={inv.status} />
                                            </div>
                                            <Button size="sm" onClick={() => payInvoice(inv.id)} data-testid={`portal-pay-${inv.id}`}>
                                                <CreditCard className="w-4 h-4 mr-1" />Pay
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-6">
                        <div className="overline mb-1">History</div>
                        <h3 className="font-heading text-lg font-semibold mb-4">Past payments</h3>
                        {paidInvoices.length === 0 ? (
                            <div className="text-sm text-muted-foreground">No payment history yet.</div>
                        ) : (
                            <div className="divide-y divide-border">
                                {paidInvoices.slice(0, 5).map((inv) => (
                                    <div key={inv.id} className="py-2.5 flex items-center justify-between text-sm">
                                        <div>
                                            <span className="font-mono">{inv.invoice_number || inv.id?.slice(0, 8)}</span>{" "}
                                            <span className="text-muted-foreground">· {fmtDate(inv.paid_at || inv.paid_date)}</span>
                                        </div>
                                        <span className="font-semibold tabular">{fmtMoney(inv.amount)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

function Action({ icon: Icon, label, testid, onClick, disabled }) {
    return (
        <button onClick={onClick} disabled={disabled}
                data-testid={testid}
                className="p-4 border border-border rounded-md text-left hover:bg-secondary/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <Icon className="w-5 h-5 text-primary mb-3" />
            <div className="text-sm font-medium">{label}</div>
        </button>
    );
}

function formatEta(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } catch {
        return "";
    }
}


/** "Paid up through" coverage snapshot. Shows one of three states:
 *   - Positive balance     → "Balance due" (amber/red)
 *   - Paid up + coverage   → "Paid up through {date}" with days-remaining
 *   - Never billed / no    → hidden (nothing to show yet)
 *     next_billing_date
 */
function CoverageBadge({ customer, openInvoices = [], onPay }) {
    const balance = parseFloat(customer.balance) || 0;
    const owes = balance > 0.5;  // ignore rounding pennies
    const nbd = customer.next_billing_date ? new Date(customer.next_billing_date) : null;
    const now = new Date();

    // Coverage runs through the day BEFORE the next bill drops.
    let coverageThrough = null;
    let daysRemaining = null;
    if (nbd && !isNaN(nbd) && nbd > now) {
        coverageThrough = new Date(nbd.getTime() - 24 * 60 * 60 * 1000);
        daysRemaining = Math.max(
            0,
            Math.ceil((coverageThrough - now) / (24 * 60 * 60 * 1000))
        );
    }

    const fmtDate = (d) =>
        d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

    // Fall-through: nothing meaningful to display.
    if (!owes && !coverageThrough) return null;

    if (owes) {
        // Pick the oldest open/overdue invoice — that's what customers
        // usually want to knock out first (it's also the one most likely
        // to go into "past due" collection).
        const oldest = [...openInvoices]
            .sort((a, b) => (a.issued_date || "").localeCompare(b.issued_date || ""))[0];
        return (
            <div
                className="rounded-lg border border-red-500/30 bg-red-500/5 px-5 py-4 flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row"
                data-testid="portal-coverage-badge"
                data-state="due"
            >
                <div className="flex items-center gap-3">
                    <ShieldAlert className="w-6 h-6 text-red-500 shrink-0" />
                    <div>
                        <div className="font-heading text-lg font-bold text-red-600 dark:text-red-400">
                            Balance due · ${balance.toFixed(2)}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                            Pay now to keep service uninterrupted. Once paid, we'll show your coverage window here.
                        </div>
                    </div>
                </div>
                {oldest && onPay && (
                    <Button
                        onClick={() => onPay(oldest.id)}
                        className="bg-red-600 hover:bg-red-700 text-white shrink-0"
                        data-testid="portal-coverage-pay-btn"
                    >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Pay ${(oldest.balance || oldest.amount || balance).toFixed(2)} now
                    </Button>
                )}
            </div>
        );
    }

    // Warn zone: less than 10 days of coverage remaining
    const warn = daysRemaining !== null && daysRemaining <= 10;
    return (
        <div
            className={
                "rounded-lg border px-5 py-4 flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row " +
                (warn
                    ? "border-amber-500/30 bg-amber-500/5"
                    : "border-emerald-500/30 bg-emerald-500/5")
            }
            data-testid="portal-coverage-badge"
            data-state={warn ? "expiring" : "paid"}
        >
            <div className="flex items-center gap-3">
                <ShieldCheck
                    className={"w-6 h-6 shrink-0 " + (warn ? "text-amber-500" : "text-emerald-500")}
                />
                <div>
                    <div className="font-heading text-lg font-bold">
                        You're paid up through{" "}
                        <span className={warn ? "text-amber-600 dark:text-amber-400" : "text-emerald-600 dark:text-emerald-400"}>
                            {fmtDate(coverageThrough)}
                        </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        {daysRemaining === 0
                            ? "Your next invoice drops tomorrow."
                            : `${daysRemaining} day${daysRemaining === 1 ? "" : "s"} of service remaining.`}
                        {" "}Next invoice: {fmtDate(new Date(customer.next_billing_date))}.
                    </div>
                </div>
            </div>
            {warn && (
                <span
                    className="text-[11px] px-2 py-1 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 font-semibold uppercase tracking-wider whitespace-nowrap"
                    data-testid="portal-coverage-expiring-pill"
                >
                    Renewing soon
                </span>
            )}
        </div>
    );
}
